import { useState } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Smartphone, Globe } from 'lucide-react';
import { ComingSoonModal } from '@/components/ui/ComingSoonModal';

/* ── Per-project visual identity — accent/glow/gradient ─────────── */
const projects = [
  {
    id: 1,
    title: 'École360',
    category: 'SaaS · Éducation',
    description: 'Plateforme de gestion scolaire complète avec tableaux de bord intelligents et suivi en temps réel.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c361eac-5927-49fe-bcdc-358a1c6675cf/images/128526920e635c38eb4c93502354627b8687190c395b9359caa033ff2059ec8e.png',
    icon: Globe,
    tags: ['React', 'Node.js', 'MongoDB'],
    accentFrom: '#00f2fe',
    accentTo: '#4facfe',
    glow: 'rgba(0,242,254,0.22)',
    available: true,
  },
  {
    id: 2,
    title: 'gencoPro SaaS',
    category: 'Outils professionnels',
    description: "Suite d'outils de productivité pour équipes avec collaboration en temps réel et automatisations.",
    image: 'https://static.prod-images.emergentagent.com/jobs/9c361eac-5927-49fe-bcdc-358a1c6675cf/images/83a19907837f43a5022d2dd96d3b45b041e50110442d1f811f55a7f395eb4cb0.png',
    icon: Globe,
    tags: ['Vue.js', 'Python', 'PostgreSQL'],
    accentFrom: '#4facfe',
    accentTo: '#38bdf8',
    glow: 'rgba(79,172,254,0.22)',
    available: true,
  },
  {
    id: 3,
    title: 'TechGuard AI',
    category: 'Application mobile',
    description: "Application de cybersécurité alimentée par l'IA pour la protection des appareils mobiles.",
    image: 'https://static.prod-images.emergentagent.com/jobs/9c361eac-5927-49fe-bcdc-358a1c6675cf/images/254bc00b9dab632d3c0d09d50d5ef1eb739702955eca97a13a4f0df0053e516c.png',
    icon: Smartphone,
    tags: ['React Native', 'TensorFlow', 'AWS'],
    accentFrom: '#a855f7',
    accentTo: '#7c3aed',
    glow: 'rgba(168,85,247,0.22)',
    available: false,
  },
  {
    id: 4,
    title: 'gencoPro App',
    category: 'Application mobile',
    description: 'Version mobile de gencoPro avec synchronisation cloud et mode hors ligne.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c361eac-5927-49fe-bcdc-358a1c6675cf/images/56e47faa00a296eb3a284c2983e9a59468e4ba494239957dc5584abcd1fa9b83.png',
    icon: Smartphone,
    tags: ['Flutter', 'Firebase', 'GraphQL'],
    accentFrom: '#ec4899',
    accentTo: '#f97316',
    glow: 'rgba(236,72,153,0.22)',
    available: false,
  },
];

export const Portfolio = () => {
  /* ── État modal — logique préservée intacte ── */
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleProjectClick = (projectTitle) => {
    if (projectTitle === 'École360') {
      window.open('https://ecole360.monaliapro.com', '_blank');
    } else if (projectTitle === 'gencoPro SaaS') {
      window.open('https://genco.monaliapro.com', '_blank');
    } else {
      setIsModalOpen(true);
    }
  };

  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="relative py-24 sm:py-36"
    >
      {/* Section ambient — large soft ellipse */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[600px] pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse, rgba(79,172,254,0.055) 0%, transparent 65%)',
          filter: 'blur(80px)',
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ── Section Header ── */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 sm:mb-20"
        >
          <span className="inline-block text-[#4facfe] text-xs font-semibold tracking-[0.2em] uppercase mb-4">
            Notre travail
          </span>
          <h2 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-5">
            Nos <span className="text-gradient">Réalisations</span>
          </h2>
          <p className="text-white/65 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            Des applications pensées pour transformer vos défis en opportunités.
            Cliquez pour découvrir chaque solution.
          </p>
          <div className="section-glow-divider max-w-xs mx-auto mt-8" />
        </motion.div>

        {/* ── Projects Grid ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-7 lg:gap-8">
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              data-testid={`portfolio-card-${project.id}`}
              initial={{ opacity: 0, y: 36 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.55, delay: index * 0.12, ease: 'easeOut' }}
              whileHover={{ y: -8, transition: { type: 'spring', stiffness: 260, damping: 20 } }}
              onClick={() => handleProjectClick(project.title)}
              className="group cursor-pointer relative"
            >
              {/* ── Card shell ── */}
              <div
                className="relative overflow-hidden rounded-2xl h-full transition-[box-shadow,border-color] duration-500"
                style={{
                  background: 'linear-gradient(160deg, rgba(10,18,42,0.90) 0%, rgba(6,10,28,0.95) 100%)',
                  border: '1px solid rgba(255,255,255,0.09)',
                  backdropFilter: 'blur(24px)',
                  boxShadow: '0 8px 40px rgba(0,0,0,0.45), 0 0 0 0.5px rgba(255,255,255,0.04) inset',
                }}
              >
                {/* Hover: accent glow border + shadow */}
                <div
                  className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none z-20"
                  style={{
                    boxShadow: `0 0 0 1px ${project.accentFrom}50, 0 24px 70px ${project.glow}, inset 0 0 80px ${project.accentFrom}06`,
                  }}
                />

                {/* Top accent line */}
                <div
                  className="absolute top-0 left-0 right-0 h-[2px] z-10 pointer-events-none"
                  style={{
                    background: `linear-gradient(to right, transparent 5%, ${project.accentFrom}cc, ${project.accentTo}cc, transparent 95%)`,
                  }}
                />

                {/* ── Image section ── */}
                <div className="relative aspect-video overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.07]"
                    style={{ outline: '1px solid rgba(255,255,255,0.07)' }}
                  />

                  {/* Dark gradient overlay */}
                  <div
                    className="absolute inset-0"
                    style={{
                      background: 'linear-gradient(to top, rgba(6,10,28,0.96) 0%, rgba(6,10,28,0.25) 50%, rgba(6,10,28,0.05) 100%)',
                    }}
                  />

                  {/* Accent color tint on hover */}
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-600 pointer-events-none"
                    style={{
                      background: `linear-gradient(to top, ${project.accentFrom}1a 0%, transparent 55%)`,
                    }}
                  />

                  {/* Category badge — top left */}
                  <div className="absolute top-4 left-4 z-10">
                    <span
                      className="inline-flex items-center px-3 py-1.5 rounded-full text-[11px] font-semibold tracking-wide text-white backdrop-blur-md"
                      style={{
                        background: `linear-gradient(135deg, ${project.accentFrom}2e, ${project.accentTo}1a)`,
                        border: `1px solid ${project.accentFrom}4a`,
                        boxShadow: `0 2px 12px rgba(0,0,0,0.3)`,
                      }}
                    >
                      {project.category}
                    </span>
                  </div>

                  {/* Type icon — top right */}
                  <div
                    className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full flex items-center justify-center backdrop-blur-md"
                    style={{
                      background: 'rgba(0,0,0,0.38)',
                      border: `1px solid ${project.accentFrom}35`,
                      boxShadow: `0 0 14px ${project.glow}`,
                    }}
                  >
                    <project.icon className="w-5 h-5" style={{ color: project.accentFrom }} />
                  </div>

                  {/* Status badge — bottom left */}
                  <div className="absolute bottom-4 left-4 z-10">
                    {project.available ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold text-emerald-300 bg-emerald-500/[0.15] border border-emerald-500/25 backdrop-blur-sm">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                        Disponible
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold text-amber-300 bg-amber-500/[0.12] border border-amber-400/20 backdrop-blur-sm">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                        Bientôt
                      </span>
                    )}
                  </div>

                  {/* External link icon — bottom right, fade in on hover */}
                  <div
                    className="absolute bottom-4 right-4 z-10 w-9 h-9 rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-[opacity,transform] duration-300 translate-y-2 group-hover:translate-y-0 backdrop-blur-md"
                    style={{
                      background: `linear-gradient(135deg, ${project.accentFrom}30, ${project.accentTo}18)`,
                      border: `1px solid ${project.accentFrom}50`,
                    }}
                  >
                    <ExternalLink className="w-4 h-4 text-white/85" />
                  </div>
                </div>

                {/* ── Content area ── */}
                <div className="p-6 sm:p-7 pt-5 sm:pt-6">
                  <h3
                    className="font-outfit text-xl sm:text-2xl font-bold text-white mb-2.5 transition-all duration-300"
                    style={{}}
                  >
                    <span className="group-hover:text-gradient transition-all duration-300 inline-block">
                      {project.title}
                    </span>
                  </h3>

                  <p className="text-white/62 text-sm leading-relaxed mb-5">
                    {project.description}
                  </p>

                  {/* Tech tags — accent-tinted */}
                  <div className="flex flex-wrap gap-2">
                    {project.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 rounded-full text-xs font-medium transition-[background-color,border-color] duration-200"
                        style={{
                          background: `${project.accentFrom}12`,
                          border: `1px solid ${project.accentFrom}30`,
                          color: `${project.accentFrom}dd`,
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Inner top-left glass shine */}
                <div
                  className="absolute inset-[1px] rounded-[15px] pointer-events-none"
                  style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.045) 0%, transparent 55%)',
                    borderTop: '1px solid rgba(255,255,255,0.09)',
                    borderLeft: '1px solid rgba(255,255,255,0.05)',
                  }}
                />
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* ── Modale intacte ── */}
      <ComingSoonModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </section>
  );
};

export default Portfolio;
